import React, { useMemo } from 'react';
import FieldRow from 'components/FormElements/FieldRow';
import TextField from '@mui/material/TextField';
import CkEditor from 'components/CkEditor';
import CompanyCard from '../Common/CompanyCard';
import RecordView from 'components/RecordView';
import Dropzone from 'components/Dropzone';
import useFileUpload from 'hooks/ui/useFileUpload';
import { UploadType } from 'further-types/files';
import { useNotification } from 'hooks/ui/useNotification';
import { UploadedFile } from 'components/Dropzone';
import { sumBy } from 'lodash';

type Props = any; //TODO: define props

const maxAttachmentSize = 10 * 1024 * 1024; // 10MB limit per file
const maxTotalAttachmentSize = 30 * 1024 * 1024; // 30MB total limit for SendGrid

const AddDeploymentUpdateDetails: React.FC<Props> = ({
  errorText,
  classes,
  data,
  setData,
  company,
}) => {
  const { uploadFile } = useFileUpload();
  const { error } = useNotification();

  // Calculate total file size of current attachments
  const totalFileSize = useMemo(() => {
    if (!data?.fileAttachments?.length) return 0;
    return sumBy(data?.fileAttachments, 'size');
  }, [data?.fileAttachments]);

  const uploadMedia = async (file: UploadedFile) => {
    const { data: uploadData, error: uploadError } = await uploadFile(
      file,
      UploadType.DeploymentUpdateAttachments,
    );
    if (uploadData) {
      return uploadData.filePath;
    } else {
      error(uploadError.message);
    }
  };

  const handleChangeAttachment = async (files: Array<UploadedFile>) => {
    // Calculate total size including new files
    const currentTotalSize = totalFileSize;
    const newFilesTotalSize = sumBy(files, 'size');
    const combinedTotalSize = currentTotalSize + newFilesTotalSize;

    // Check total size limit
    if (combinedTotalSize > maxTotalAttachmentSize) {
      error(
        `Total file size cannot exceed ${Math.round(
          maxTotalAttachmentSize / (1024 * 1024),
        )}MB. Current total: ${Math.round(
          combinedTotalSize / (1024 * 1024),
        )}MB`,
      );
      return;
    }

    const filesToUpload = await Promise.all(
      files.map(async (file) => {
        if (file.path) {
          const filePath = await uploadMedia(file);
          if (!filePath) return;
          return {
            name: file?.name,
            path: filePath,
            preview: file?.preview,
            size: file.size,
          };
        }
      }),
    );
    setData({
      ...data,
      fileAttachments: [
        ...(data.fileAttachments || []),
        ...filesToUpload.filter((file) => Boolean(file)),
      ],
    });
  };

  const handleRemoveAttachment = (index: number) => {
    setData({
      ...data,
      fileAttachments: data.fileAttachments.filter((_, idx) => idx !== index),
    });
  };
  return (
    <RecordView>
      <FieldRow title="Subject line*">
        <TextField
          required
          name="subject"
          fullWidth
          placeholder="e.g. You have a new company in your portfolio"
          variant="outlined"
          value={data?.subject}
          onChange={(event) =>
            setData({ ...data, subject: event.target.value })
          }
          error={errorText?.subject}
          helperText={errorText?.subject}
        />
      </FieldRow>
      <FieldRow title="Email title*">
        <TextField
          required
          name="title"
          fullWidth
          placeholder="e.g. Meet your new portfolio company: Company A"
          variant="outlined"
          value={data?.title}
          onChange={(event) => setData({ ...data, title: event.target.value })}
          error={errorText?.title}
          helperText={errorText?.title}
        />
      </FieldRow>
      <FieldRow title="Email top paragraph*" introLineTopTitle>
        Dear $recipientname,
        <CkEditor
          placeholder={`We're excited to let you know that you have a new company in your portfolio.`}
          value={data?.emailTopParagraph}
          onChange={(value) => {
            setData({
              ...data,
              emailTopParagraph: value,
            });
          }}
          onBlur={(value) => {
            setData({
              ...data,
              emailTopParagraph: value,
            });
          }}
        />
        <p className={classes?.errorCk}>{errorText?.emailTopParagraph}</p>
      </FieldRow>
      <CompanyCard company={company} />
      <FieldRow title="Email bottom paragraph and sign-off*" topTitle>
        <CkEditor
          placeholder={`We're excited to let you know that you have a new company in your portfolio.`}
          value={data?.emailBottomParagraph}
          onChange={(value) => {
            setData({
              ...data,
              emailBottomParagraph: value,
            });
          }}
          onBlur={(value) => {
            setData({
              ...data,
              emailBottomParagraph: value,
            });
          }}
        />
        <p className={classes?.errorCk}>{errorText?.emailBottomParagraph}</p>
      </FieldRow>
      <FieldRow title="Upload email attachment">
        <Dropzone
          name="attachment"
          defaultAcceptGroup="filesAndImages"
          files={
            data.fileAttachments && data.fileAttachments.length > 0
              ? data.fileAttachments.map((attachment) => ({
                  fileName: attachment.name,
                  preview: attachment.preview || attachment.path,
                }))
              : []
          }
          onDropFile={handleChangeAttachment}
          onRemove={handleRemoveAttachment}
          maxFiles={10}
          maxFileSize={maxAttachmentSize}
        />
        {errorText?.fileAttachments && (
          <p className={classes?.errorCk}>{errorText.fileAttachments}</p>
        )}
      </FieldRow>
    </RecordView>
  );
};
export default AddDeploymentUpdateDetails;
